import { useEffect, useRef, useState } from 'react';
import { lookupUserByEmail, UserLookupResult } from './api';

interface UseEmailLookupInput {
  email: string;
  companyId: number | undefined;
  /** só dispara lookup quando a UI está em modo "novo" — em edição não faz sentido */
  enabled: boolean;
}

interface UseEmailLookupState {
  loading: boolean;
  result: UserLookupResult | null;
  error: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEBOUNCE_MS = 500;

/**
 * Hook que consulta GET /users/lookup-by-email com debounce quando o
 * email digitado é válido. Reseta em cada mudança de email/company.
 *
 * O caller decide QUANDO renderizar UI baseada em `result.found` —
 * o hook não gerencia UI, só o estado da consulta.
 */
export function useEmailLookup({ email, companyId, enabled }: UseEmailLookupInput): UseEmailLookupState {
  const [state, setState] = useState<UseEmailLookupState>({
    loading: false, result: null, error: null,
  });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!enabled || !companyId || !email || !EMAIL_RE.test(email)) {
      setState({ loading: false, result: null, error: null });
      return;
    }

    setState((s) => ({ ...s, loading: true, error: null }));
    const myRequest = ++requestIdRef.current;

    timerRef.current = setTimeout(async () => {
      try {
        const result = await lookupUserByEmail(email.trim().toLowerCase(), companyId);
        if (requestIdRef.current !== myRequest) return; // outra query saiu na frente
        setState({ loading: false, result, error: null });
      } catch (err: any) {
        if (requestIdRef.current !== myRequest) return;
        setState({
          loading: false,
          result: null,
          error: err?.response?.data?.message ?? err?.message ?? 'lookup failed',
        });
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [email, companyId, enabled]);

  return state;
}
